import React, { useEffect, useState } from "react";

export default function MarketExecutionOverview() {
  const [loading, setLoading] = useState(true);

  const [auditData] = useState({
    surveysAssigned: 420,
    surveysCompleted: 348,
    surveysPending: 72,
    complianceScore: 84,
    auditViolations: 26,
    avgAuditScore: 78,
  });

  const [merchandisingData] = useState({
    posmInstalled: 1840,
    visibilityScore: 88,
    displayCompliance: 81,
    damagedBranding: 24,
  });

  const [feedbackData] = useState({
    openIssues: 38,
    resolved: 124,
    escalated: 12,
    avgResolution: "4.2h",
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1800);

    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return <MarketExecutionSkeleton />;
  }

  return (
    <>
      {/* SHIMMER */}

      <style>
        {`
          @keyframes shimmer {
            0% {
              background-position: -400px 0;
            }

            100% {
              background-position: 400px 0;
            }
          }
        `}
      </style>

      {/* SECTION HEADER */}

      <SectionHeader icon="📝" title="Audit / Merchandising / Feedback" />

      {/* MAIN GRID */}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.1fr 1fr",
          gap: 14,
        }}
      >
        {/* -------------------------------------------------------------------------- */}
        {/* LEFT SIDE */}
        {/* -------------------------------------------------------------------------- */}

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
        >
          {/* AUDIT OVERVIEW */}

          <div
            style={{
              background: "#fff",
              border: "1px solid #E2E8F0",
              borderRadius: 18,
              padding: 18,
              boxShadow: "0 2px 10px rgba(15,23,42,0.03)",
            }}
          >
            {/* HEADER */}

            <SubHeader icon="📋" title="Audit / Survey Overview" />

            {/* KPI GRID */}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 10,
                marginBottom: 20,
              }}
            >
              <MiniMetricCard
                label="Assigned"
                value={auditData.surveysAssigned}
                color="#2563EB"
              />

              <MiniMetricCard
                label="Completed"
                value={auditData.surveysCompleted}
                color="#059669"
              />

              <MiniMetricCard
                label="Pending"
                value={auditData.surveysPending}
                color="#EA580C"
              />

              <MiniMetricCard
                label="Compliance"
                value={`${auditData.complianceScore}%`}
                color="#10B981"
              />

              <MiniMetricCard
                label="Violations"
                value={auditData.auditViolations}
                color="#E11D48"
              />

              <MiniMetricCard
                label="Avg Score"
                value={`${auditData.avgAuditScore}/100`}
                color="#475569"
              />
            </div>

            {/* COMPLIANCE */}

            <ComplianceBar
              label="Hygiene & Cleanliness"
              value={88}
              color="#10B981"
            />

            <ComplianceBar label="Product Display" value={76} color="#6366F1" />

            <ComplianceBar
              label="Pricing Compliance"
              value={92}
              color="#0EA5E9"
            />

            <ComplianceBar
              label="Stock Availability"
              value={71}
              color="#FB923C"
            />
          </div>

          {/* FEEDBACK */}
        </div>

        {/* -------------------------------------------------------------------------- */}
        {/* RIGHT SIDE */}
        {/* -------------------------------------------------------------------------- */}

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
        >
          {/* MERCHANDISING */}

          <div
            style={{
              background: "#fff",
              border: "1px solid #E2E8F0",
              borderRadius: 18,
              padding: 18,
              boxShadow: "0 2px 10px rgba(15,23,42,0.03)",
              flex: 1,
            }}
          >
            <SubHeader icon="🛒" title="Merchandising Overview" />

            {/* TOP KPI */}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
                marginBottom: 22,
              }}
            >
              <MerchCard
                label="POSM Installed"
                value={merchandisingData.posmInstalled}
                color="#2563EB"
              />

              <MerchCard
                label="Visibility Score"
                value={`${merchandisingData.visibilityScore}%`}
                color="#059669"
              />

              <MerchCard
                label="Display Compliance"
                value={`${merchandisingData.displayCompliance}%`}
                color="#8B5CF6"
              />

              <MerchCard
                label="Damaged Branding"
                value={merchandisingData.damagedBranding}
                color="#E11D48"
              />
            </div>
          </div>

          <div
            style={{
              background: "#fff",
              border: "1px solid #E2E8F0",
              borderRadius: 18,
              padding: 18,
              boxShadow: "0 2px 10px rgba(15,23,42,0.03)",
            }}
          >
            <SubHeader icon="💬" title="Feedback & Issues" />

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
              }}
            >
              <FeedbackCard
                title="Open Issues"
                value={feedbackData.openIssues}
                color="#E11D48"
              />

              <FeedbackCard
                title="Resolved"
                value={feedbackData.resolved}
                color="#059669"
              />

              <FeedbackCard
                title="Escalated"
                value={feedbackData.escalated}
                color="#EA580C"
              />

              <FeedbackCard
                title="Avg Resolution"
                value={feedbackData.avgResolution}
                color="#2563EB"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* SUB HEADER */
/* -------------------------------------------------------------------------- */

function SubHeader({ icon, title }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 16,
      }}
    >
      <span style={{ fontSize: 16 }}>{icon}</span>

      <span
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: "#334155",
          letterSpacing: "0.05em",
          textTransform: "uppercase",
        }}
      >
        {title}
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* SECTION HEADER */
/* -------------------------------------------------------------------------- */

function SectionHeader({ icon, title }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 14,
        marginTop: 24,
        paddingBottom: 10,
        borderBottom: "1px solid #E2E8F0",
      }}
    >
      <span style={{ fontSize: 18 }}>{icon}</span>

      <span
        style={{
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "#0EA5E9",
        }}
      >
        {title}
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* MINI METRIC CARD */
/* -------------------------------------------------------------------------- */

function MiniMetricCard({ label, value, color }) {
  return (
    <div
      style={{
        border: `1px solid ${color}25`,
        borderRadius: 14,
        padding: 14,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: "#94A3B8",
          textTransform: "uppercase",
          marginBottom: 10,
          letterSpacing: "0.06em",
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontSize: 24,
          fontWeight: 700,
          color,
        }}
      >
        {value}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* COMPLIANCE BAR */
/* -------------------------------------------------------------------------- */

function ComplianceBar({ label, value, color }) {
  return (
    <div
      style={{
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 6,
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "#334155",
          }}
        >
          {label}
        </span>

        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            color,
          }}
        >
          {value}/100
        </span>
      </div>

      <div
        style={{
          height: 7,
          background: "#E2E8F0",
          borderRadius: 999,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${value}%`,
            height: "100%",
            background: color,
            borderRadius: 999,
          }}
        />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* MERCH CARD */
/* -------------------------------------------------------------------------- */

function MerchCard({ label, value, color }) {
  return (
    <div
      style={{
        border: `1px solid ${color}25`,
        borderRadius: 16,
        padding: 16,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: "#94A3B8",
          marginBottom: 10,
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontSize: 26,
          fontWeight: 700,
          color,
        }}
      >
        {value}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* FEEDBACK CARD */
/* -------------------------------------------------------------------------- */

function FeedbackCard({ title, value, color }) {
  return (
    <div
      style={{
        border: `1px solid ${color}25`,
        borderRadius: 14,
        padding: 16,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: "#94A3B8",
          marginBottom: 10,
          textTransform: "uppercase",
        }}
      >
        {title}
      </div>

      <div
        style={{
          fontSize: 24,
          fontWeight: 700,
          color,
        }}
      >
        {value}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* INSIGHT CARD */
/* -------------------------------------------------------------------------- */

function InsightCard({ title, color, text }) {
  return (
    <div
      style={{
        background: color,
        borderRadius: 14,
        padding: 14,
        marginBottom: 10,
      }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: text,
        }}
      >
        {title}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* SKELETON */
/* -------------------------------------------------------------------------- */

function MarketExecutionSkeleton() {
  return (
    <div
      style={{
        height: 500,
        borderRadius: 18,
        background: "linear-gradient(90deg,#E2E8F0,#F8FAFC,#E2E8F0)",
        backgroundSize: "800px 100%",
        animation: "shimmer 1.5s infinite linear",
      }}
    />
  );
}
